import classNames from "classnames";
import { keys } from "lodash";
import PropTypes from "prop-types";
import { Col, Container, Row } from "react-bootstrap";
import { connect } from "react-redux";
import { compose, lifecycle, withProps } from "recompose";
import { Field, propTypes as ReduxFormPropTypes } from "redux-form";

import { FormControl } from "@dpdgroupuk/mydpd-ui";
import { withTrack, withTrackProps } from "@dpdgroupuk/react-event-tracker";

import CardWithTitle from "../../../../components/CardWithTitle";
import { ALTERNATIVE_ADDRESS_SERVICE } from "../../../../constants/analytics";
import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DATE_FORMAT_REGEXP,
} from "../../../../constants/dateFormats";
import {
  alternativeAddressUpgrade as Fields,
  INTERNATIONAL_POSTCODE_MAX_LENGTH,
} from "../../../../constants/forms";
import * as M from "../../../../constants/strings";
import PostcodeFinderInput from "../../../../features/PostcodeFinder/PostcodeFinderInput";
import {
  getNotificationDetails,
  isParcelCode,
} from "../../../../models/parcel";
import { deliveryEditValidators } from "../../../../models/validators";
import {
  checkMinDate,
  clearAlternativeDeliveryDates,
  clearDeliveryService,
  getAlternativeTimesAction,
  onAlternativeAddressService,
} from "../../actions";
import EditPanel from "../../components/EditPanel";
import TermAndInstructions from "../../components/TermsAndInstructions";
import { hadleInputFocus, withAddressEdit, withParcelEdit } from "../../hocs";
import { withRelatedParcelsInitialValues } from "../../hocs/withRelatedParcelsInitialValues";
import {
  getActionDates,
  getAlternativeTimes,
  getDeliveryDateOpenToDate,
  getParcelFormStateValues,
  isDeliveryDateDisabled,
} from "../../selectors";
import styles from "./AlternativeAddress.module.scss";

const AlternativeAddressAndService = ({
  handleSubmit,
  onSubmit,
  onPostcodeFieldBlur,
  onAddressInput,
  onFindPostcode,
  onSelectDate,
  onSelectService,
  onSelectAddress,
  getParcelAddressPoint,
  error,
  alternativeService,
  onAddressSelect,
  formValues,
  preventDatePickerBlur,
  setIsDatePickerOpen,
  dates,
  getDatesRange,
  filterDate,
  isDeliveryDateDisabled,
  displayPostcodeFinder,
  isPostcodeRequired,
  countries,
  openToDate,
  ...rest
}) => (
  <Col className="col-12 p-0">
    <CardWithTitle title={M.CHANGE_ADDRESS_UPGRADE}>
      <Row>
        <Col className="col-12 col-md-6">
          <Field
            autoFocus={false}
            name={Fields.COUNTRY}
            component={FormControl.Input}
            label={M.COUNTRY}
            disabled={true}
            maxLength={255}
          />
          <Field
            autoFocus={false}
            name={Fields.POSTCODE}
            component={PostcodeFinderInput}
            label={M.POSTCODE}
            maxLength={INTERNATIONAL_POSTCODE_MAX_LENGTH}
            onBlur={(obj, postcode) => onPostcodeFieldBlur(postcode, countries)}
            onFind={onFindPostcode}
            onSelectAddress={onSelectAddress}
            onSelectionChange={onAddressSelect}
            onFocus={onAddressInput}
            getParcelAddressPoint={getParcelAddressPoint}
            labelKey={option => option.postcode}
            withAutocomplete={displayPostcodeFinder}
            showFindButton={displayPostcodeFinder}
            required={isPostcodeRequired}
            id={Fields.POSTCODE}
          />
          <Field
            autoFocus={false}
            name={Fields.NAME}
            component={FormControl.Input}
            label={M.NAME}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
            required
          />
          <Field
            autoFocus={false}
            name={Fields.STREET}
            component={FormControl.Input}
            label={M.STREET}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
            required
          />
          <Field
            autoFocus={false}
            name={Fields.SUBURB}
            component={FormControl.Input}
            label={M.SUBURB}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
          />
          <Field
            autoFocus={false}
            name={Fields.REFERENCE_CHANGE_ADDRESS}
            component={FormControl.Input}
            label={M.REFERENCE}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
          />
        </Col>
        <Col className={classNames("col-12 col-md-6", styles.inputContainer)}>
          <Field
            autoFocus={false}
            name={Fields.TOWN}
            component={FormControl.Input}
            label={M.TOWN}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
          />
          <Field
            autoFocus={false}
            name={Fields.CITY}
            component={FormControl.Input}
            label={M.CITY}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
            required
          />
          <Field
            autoFocus={false}
            name={Fields.COUNTY}
            component={FormControl.Input}
            label={M.COUNTY}
            maxLength={35}
            helperText={M.MAX_35_CHARACTERS}
          />
          <Field
            name={Fields.DELIVERY_DATE}
            component={FormControl.DatePicker}
            placeholder={M.PLEASE_SELECT_DATE}
            label={M.DELIVERY_DATE}
            onChange={onSelectDate}
            disabled={isDeliveryDateDisabled}
            // NOTE: getDatesRange is required to forbid change month by clicking header arrows and set proper date if date was changed
            getDatesRange={getDatesRange}
            filterDate={filterDate}
            dateFormat={DEFAULT_DATE_FORMAT}
            onDatePickerChange={setIsDatePickerOpen}
            onBlur={e => preventDatePickerBlur(e)}
            openToDate={openToDate}
            required
          />
          <Field
            name={Fields.DELIVERY_SERVICE}
            component={FormControl.Dropdown}
            label={M.DELIVERY_SERVICE.toUpperCase()}
            values={alternativeService}
            onChange={onSelectService}
            disabled={!alternativeService.length}
            required
          />
        </Col>
      </Row>
      <EditPanel
        onSubmit={handleSubmit(onSubmit)}
        error={error}
        offContactDetailsValidation={true}
        additionalInfoComponent={
          <Container>
            <TermAndInstructions />
          </Container>
        }
        {...rest}
      />
    </CardWithTitle>
  </Col>
);

AlternativeAddressAndService.defaultProps = {
  onSubmit: () => null,
  getDates: () => null,
  toggleAllCheckboxes: () => null,
  onChangeCheckbox: () => null,
  onFindPostcode: () => null,
  onAddressInput: () => null,
  onSelectDate: () => null,
  onSelectService: () => null,
  onSelectAddress: () => null,
  getParcelAddressPoint: () => null,
  dates: [],
  alternativeService: [],
};

AlternativeAddressAndService.propTypes = {
  getDates: PropTypes.func,
  toggleAllCheckboxes: PropTypes.func,
  onChangeCheckbox: PropTypes.func,
  onFindPostcode: PropTypes.func,
  onAddressInput: PropTypes.func,
  onSelectDate: PropTypes.func,
  onSelectService: PropTypes.func,
  onSelectAddress: PropTypes.func,
  getParcelAddressPoint: PropTypes.func,
  alternativeService: PropTypes.arrayOf(PropTypes.object),
  formValues: PropTypes.object,
  dates: PropTypes.arrayOf(PropTypes.string),
  getDatesRange: PropTypes.func,
  filterDate: PropTypes.func,
  isDeliveryDateDisabled: PropTypes.bool,
  onPostcodeFieldBlur: PropTypes.func,
  displayPostcodeFinder: PropTypes.bool,
  countries: PropTypes.array,
  ...ReduxFormPropTypes,
};

export default compose(
  withRelatedParcelsInitialValues,
  withProps(({ additionalInitialValues, parcel }) => ({
    additionalInitialValues: {
      ...additionalInitialValues,
      ...getNotificationDetails(parcel),
      country: parcel.deliveryDetails.address.countryName,
      countryCode: parcel.deliveryDetails.address.countryCode,
    },
  })),
  connect(
    state => ({
      dates: getActionDates(state),
      alternativeService: getAlternativeTimes(state),
      formValues: getParcelFormStateValues(state),
      isDeliveryDateDisabled: isDeliveryDateDisabled(state),
      openToDate: getDeliveryDateOpenToDate(state),
    }),
    (dispatch, { match, parcel }) => ({
      getParcelAddressPoint: () => parcel.deliveryDetails.address.addressPoint,
      onSelectDate: date => {
        dispatch(clearDeliveryService());
        DEFAULT_DATE_FORMAT_REGEXP.test(date) &&
          dispatch(
            getAlternativeTimesAction(match.params.actionCode, parcel, date)
          );
      },
    })
  ),
  withAddressEdit,
  withParcelEdit({
    onSubmit: (parcelCode, values) => {
      const parcelCodes = keys(values).filter(isParcelCode);
      const query = {
        sendersReference: values.sendersReference,
        instructions: values.instructions,
        deliveryDate: values.deliveryDate,
        parcelCodes,
        notificationDetails: {
          mobile: values.mobile,
          email: values.email,
          contactName: values.contactName,
        },
        address: {
          city: values.city,
          countryCode: values.countryCode,
          county: values.county,
          organisation: values.name,
          postcode: values.postcode,
          street: values.street,
          town: values.town,
          property: values.suburb,
        },
        serviceCode: values.deliveryService,
      };
      return onAlternativeAddressService(parcelCode, query);
    },
    validate: deliveryEditValidators.alternativeAddressAndService,
  }),
  withTrack(ALTERNATIVE_ADDRESS_SERVICE.LOAD),
  withTrackProps({
    onSubmit: ALTERNATIVE_ADDRESS_SERVICE.CLICK_SUBMIT,
    onFindPostcode: ALTERNATIVE_ADDRESS_SERVICE.CLICK_FIND_POSTCODE,
    onInputFocus: fieldName => {
      const field = isParcelCode(fieldName) ? Fields.PARCEL_CODE : fieldName;
      switch (field) {
        case Fields.POSTCODE:
          return ALTERNATIVE_ADDRESS_SERVICE.POSTCODE_INPUT;
        case Fields.AGREEMENT:
          return ALTERNATIVE_ADDRESS_SERVICE.CLICK_TERMS_CHECKBOX;
        case Fields.DELIVERY_DATE:
          return ALTERNATIVE_ADDRESS_SERVICE.CLICK_DATE_DROP_DOWN_LIST;
        case Fields.DELIVERY_SERVICE:
          return ALTERNATIVE_ADDRESS_SERVICE.CLICK_DELIVERY_SERVICE_DROP_DOWN_LIST;
        case Fields.REFERENCE_CHANGE_ADDRESS:
          return ALTERNATIVE_ADDRESS_SERVICE.REFERENCE_INPUT;
        case Fields.INSTRUCTIONS:
          return ALTERNATIVE_ADDRESS_SERVICE.INSTRUCTIONS_INPUT;
        case Fields.CONTACT_NAME:
          return ALTERNATIVE_ADDRESS_SERVICE.CONTACT_NAME_INPUT;
        case Fields.MOBILE:
          return ALTERNATIVE_ADDRESS_SERVICE.MOBILE_NUMBER_INPUT;
        case Fields.EMAIL:
          return ALTERNATIVE_ADDRESS_SERVICE.EMAIL_ADDRESS_INPUT;
        case Fields.ALL_CHECKBOXES:
          return ALTERNATIVE_ADDRESS_SERVICE.CLICK_PARCEL_NO_CHECKBOX_SELECT_ALL;
        case Fields.PARCEL_CODE:
          return ALTERNATIVE_ADDRESS_SERVICE.CLICK_PARCEL_NO_CHECKBOX;
        default:
          break;
      }
    },
    onAddressInput: ALTERNATIVE_ADDRESS_SERVICE.CLICK_ADDRESS_INPUT,
    onSelectDate: ALTERNATIVE_ADDRESS_SERVICE.CLICK_DATE,
    onSelectService: ALTERNATIVE_ADDRESS_SERVICE.CLICK_DELIVERY_SERVICE,
    onSelectAddress: ALTERNATIVE_ADDRESS_SERVICE.CLICK_ADDRESS,
  }),
  hadleInputFocus,
  lifecycle({
    componentDidUpdate() {
      this.props.dispatch(checkMinDate());
    },
    componentWillUnmount() {
      this.props.dispatch(clearAlternativeDeliveryDates());
    },
  })
)(AlternativeAddressAndService);
